export function parseXML(xmlString: string): Document {
  return new DOMParser().parseFromString(xmlString, 'application/xml');
}

export function xmlElementToString(xmlElement: Element | ChildNode): string | null {
  if (!xmlElement || !xmlElement.nodeType) {
    return null;
  }
  return new XMLSerializer().serializeToString(xmlElement);
}

export function extractXmlProps(element: Element | ChildNode): Record<string, string> {
  const props: Record<string, string> = {};
  for (const node of Array.from(element.childNodes || [])) {
    props[node.nodeName] = node.textContent || '';
  }
  return props;
}

export function extractItemId(item: Element | ChildNode): string {
  const element = item as Element;
  const props = extractXmlProps(element);
  return props['CustomID'] || props['ID'] || '';
}

export const upsertChildren = (element: Element, props: { [key: string]: string }): Element => {
  for (const [key, value] of Object.entries(props)) {
    const existentChildren = element.getElementsByTagName(key);
    if (existentChildren.length === 1) {
      const existentChild = existentChildren[0];
      existentChild.textContent = value;
      continue;
    }

    if (existentChildren.length > 1) {
      console.error('More than one child with the same name', key, 'in', element);
      continue;
    }

    const child = document.createElementNS(null, key);
    child.textContent = value;
    element.appendChild(child);
  }
  return element;
};

export const removeChildren = (element: Element, props: string[]): Element => {
  for (const key of props) {
    const existentChildren = element.getElementsByTagName(key);
    for (let i = existentChildren.length - 1; i >= 0; i--) {
      existentChildren[i].remove();
    }
  }
  return element;
};

export function formatXML(xml: string, tab = '\t', nl = '\n'): string {
  let formatted = '',
    indent = '';
  const nodes = xml.slice(1, -1).split(/>\s*</);
  if (nodes[0][0] == '?') formatted += '<' + nodes.shift() + '>' + nl;
  for (let i = 0; i < nodes.length; i++) {
    const node = nodes[i];
    if (node[0] == '/') indent = indent.slice(tab.length); // decrease indent
    formatted += indent + '<' + node + '>' + nl;
    if (node[0] != '/' && node[node.length - 1] != '/' && node.indexOf('</') == -1) indent += tab; // increase indent
  }
  return formatted;
}

export function rewriteChildren(
  element: Element,
  orderedFields: string[],
  updatedValues: Record<string, string>,
  excludedFields: string[] = []
): Element {
  const preservedValues = new Map<string, string>();
  Array.from(element.children).forEach((child) => {
    if (!(child.nodeName in updatedValues) && !excludedFields.includes(child.nodeName)) {
      preservedValues.set(child.nodeName, child.textContent ?? '');
    }
  });

  while (element.firstChild) {
    element.removeChild(element.firstChild);
  }

  orderedFields.forEach((fieldName) => {
    const child = document.createElementNS(null, fieldName);
    if (fieldName in updatedValues) {
      child.textContent = updatedValues[fieldName];
      element.appendChild(child);
    } else if (preservedValues.has(fieldName)) {
      child.textContent = preservedValues.get(fieldName) ?? '';
      element.appendChild(child);
    }
  });

  return element;
}

export function getPropsFrom(xml: ChildNode | Element): Record<string, string> {
  const currentXmlString = xmlElementToString(xml as Element);
  const currentXml = parseXML(currentXmlString || '');

  const root: Element = currentXml.documentElement;
  return extractXmlProps(root);
}
